import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Circle, Gauge, CalendarClock, ClipboardList, Calculator,
  Route as RouteIcon, Trophy, Quote as QuoteIcon, Flag,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { computeReadiness, computeMatch, daysUntil } from "@/lib/calc";
import { scholarships } from "@/data/scholarships";
import { quotes, roadmapPhases } from "@/data/roadmap";
import { successStories } from "@/data/successStories";
import { ProgressRing, ProgressBar } from "@/components/ProgressRing";

const domainColors = { academic: "#7C3AED", language: "#8B5CF6", experience: "#A78BFA", financial: "#C084FC", application: "#6D28D9" };

function nextActions(profile, ctx) {
  const r = computeReadiness(profile, ctx);
  const sorted = [...r.domains].sort((a, b) => a.score - b.score);
  const base = [
    { id: "act-lang", title: "Latihan IELTS Reading", done: (ctx.testAttempts || []).length > 0, priority: "High", reason: "Menutup gap bahasa lebih cepat.", deadline: null },
    { id: "act-cv", title: "Lengkapi CV akademik", done: (ctx.completedTasks || []).includes("b2"), priority: "Medium", reason: "Dokumen inti aplikasi.", deadline: null },
    { id: "act-match", title: "Cari 3 beasiswa yang cocok", done: (ctx.savedScholarships || []).length >= 3, priority: "Medium", reason: "Fokuskan target aplikasi.", deadline: null },
    { id: "act-essay", title: "Mulai menulis essay", done: (ctx.completedTasks || []).includes("b4"), priority: "High", reason: sorted[0] ? `Perkuat domain ${sorted[0].label}.` : "Persiapan aplikasi.", deadline: null },
  ];
  return base;
}

export default function Dashboard() {
  const { data, displayName, updateData } = useApp();
  const { profile } = data;
  const ctx = useMemo(
    () => ({ completedTasks: data.completedTasks, testAttempts: data.testAttempts, savedScholarships: data.savedScholarships }),
    [data.completedTasks, data.testAttempts, data.savedScholarships]
  );

  const readiness = useMemo(() => computeReadiness(profile, ctx), [profile, ctx]);
  const matches = useMemo(
    () => scholarships.map((s) => ({ s, m: computeMatch(profile, s) })).sort((a, b) => b.m.matchScore - a.m.matchScore).slice(0, 3),
    [profile]
  );
  const deadlines = useMemo(
    () => scholarships.filter((s) => s.deadline).map((s) => ({ s, d: daysUntil(s.deadline) })).filter((x) => x.d > 0).sort((a, b) => a.d - b.d).slice(0, 3),
    []
  );
  const actions = useMemo(() => nextActions(profile, ctx), [profile, ctx]);
  const quote = quotes[new Date().getDate() % quotes.length];
  const lastTest = data.testAttempts[data.testAttempts.length - 1];

  const toggleAction = (id) => {
    const key = `dash:${id}`;
    updateData((prev) => {
      const done = prev.completedTasks.includes(key);
      return { ...prev, completedTasks: done ? prev.completedTasks.filter((t) => t !== key) : [...prev.completedTasks, key] };
    });
  };
  const isDone = (a) => a.done || data.completedTasks.includes(`dash:${a.id}`);

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="dashboard-greeting">
            Halo, {displayName || "👋"} 👋
          </h1>
          <p className="mt-1 text-slate-500">Terus melangkah, dunia lebih dekat dari yang kamu kira.</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Readiness */}
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm lg:col-span-2" data-testid="readiness-card">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900">Your Scholarship Readiness</h2>
              <p className="text-xs text-slate-400">{readiness.method.version} · Confidence: {readiness.confidence} · Update: {readiness.method.updated}</p>
            </div>
            <Link to="/app/profil" className="text-sm font-semibold text-[#7C3AED] hover:underline">Perbarui →</Link>
          </div>
          <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:items-center">
            <ProgressRing value={readiness.overall} sublabel="readiness" testid="readiness-ring" />
            <div className="flex-1 space-y-3">
              {readiness.domains.map((d) => (
                <div key={d.key}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-slate-600">{d.label}</span>
                    <span className="font-bold text-slate-800">{d.score}%</span>
                  </div>
                  <ProgressBar value={d.score} color={domainColors[d.key]} className="mt-1" />
                </div>
              ))}
            </div>
          </div>
          <p className="mt-4 rounded-xl bg-purple-50/60 p-3 text-xs text-slate-500">
            Profil persiapanmu dinilai <b className="text-[#5B21B6]">{readiness.overall}%</b> di bawah kerangka readiness Scholavia. Ini <b>bukan</b> peluang diterima beasiswa.
          </p>
        </div>

        {/* Next Actions */}
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm" data-testid="next-actions-card">
          <h2 className="font-display text-lg font-bold text-slate-900">Langkah Selanjutnya</h2>
          <p className="text-xs text-slate-400">Apa yang harus kamu lakukan hari ini?</p>
          <ul className="mt-4 space-y-2">
            {actions.map((a) => {
              const done = isDone(a);
              return (
                <li key={a.id}>
                  <button onClick={() => toggleAction(a.id)} data-testid={`action-${a.id}`}
                    className="flex w-full items-start gap-2.5 rounded-xl border border-transparent p-2 text-left hover:border-purple-100 hover:bg-purple-50/40">
                    {done ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" /> : <Circle className="mt-0.5 h-5 w-5 shrink-0 text-slate-300" />}
                    <span>
                      <span className={`block text-sm font-medium ${done ? "text-slate-400 line-through" : "text-slate-700"}`}>{a.title}</span>
                      <span className="flex items-center gap-1.5 text-xs text-slate-400">
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${a.priority === "High" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>{a.priority}</span>
                        {a.reason}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Matches */}
      <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-slate-900">Beasiswa yang Cocok Untukmu</h2>
          <Link to="/app/beasiswa" className="text-sm font-semibold text-[#7C3AED] hover:underline">Lihat Semua →</Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {matches.map(({ s, m }) => (
            <Link key={s.id} to={`/app/beasiswa/${s.id}`} data-testid={`match-${s.id}`}
              className="group rounded-xl border border-purple-100 p-4 transition-all hover:-translate-y-0.5 hover:shadow-md hover:shadow-purple-900/5">
              <div className="flex items-start justify-between">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-50 text-[#7C3AED]"><Flag className="h-4 w-4" /></span>
                <span className="rounded-full bg-purple-50 px-2 py-0.5 text-xs font-bold text-[#7C3AED]">{m.matchScore}% Match</span>
              </div>
              <h3 className="mt-3 font-display text-sm font-bold text-slate-900">{s.name}</h3>
              <p className="text-xs text-slate-400">{s.country} · {s.degree}</p>
              <p className="mt-2 flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="h-3.5 w-3.5" /> {s.funding}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Row: deadlines / test / cost */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
          <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900"><CalendarClock className="h-4 w-4 text-[#7C3AED]" /> Deadline Terdekat</h2>
          <ul className="mt-3 space-y-3">
            {deadlines.map(({ s, d }) => (
              <li key={s.id} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{s.name}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${d < 30 ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>{d} hari</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
          <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900"><ClipboardList className="h-4 w-4 text-[#7C3AED]" /> Progres Test</h2>
          {lastTest ? (
            <div className="mt-3">
              <div className="flex items-end gap-2"><span className="font-display text-3xl font-extrabold text-[#5B21B6]">{lastTest.score}</span><span className="mb-1 text-sm text-slate-400">/ 100</span></div>
              <p className="text-xs text-slate-400 capitalize">{lastTest.category} test terakhir</p>
              <Link to="/app/test" className="mt-2 inline-block text-sm font-semibold text-[#7C3AED] hover:underline">Latihan lagi →</Link>
            </div>
          ) : (
            <div className="mt-3"><p className="text-sm text-slate-400">Belum ada riwayat test.</p><Link to="/app/test" className="mt-2 inline-block text-sm font-semibold text-[#7C3AED] hover:underline">Mulai simulasi →</Link></div>
          )}
        </div>
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
          <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900"><Calculator className="h-4 w-4 text-[#7C3AED]" /> Snapshot Biaya</h2>
          {data.costPrefs ? (
            <div className="mt-3">
              <div className="font-display text-2xl font-extrabold text-slate-900">{data.costPrefs.summary}</div>
              <p className="text-xs text-slate-400">{data.costPrefs.city}, {data.costPrefs.country}</p>
              <Link to="/app/kalkulator" className="mt-2 inline-block text-sm font-semibold text-[#7C3AED] hover:underline">Hitung ulang →</Link>
            </div>
          ) : (
            <div className="mt-3"><p className="text-sm text-slate-400">Belum ada estimasi biaya.</p><Link to="/app/kalkulator" className="mt-2 inline-block text-sm font-semibold text-[#7C3AED] hover:underline">Hitung sekarang →</Link></div>
          )}
        </div>
      </div>

      {/* Roadmap preview + Motivation */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900"><RouteIcon className="h-4 w-4 text-[#7C3AED]" /> Roadmap Persiapan</h2>
            <Link to="/app/roadmap" className="text-sm font-semibold text-[#7C3AED] hover:underline">Lihat Roadmap →</Link>
          </div>
          <div className="space-y-3">
            {roadmapPhases.slice(0, 2).map((p) => (
              <div key={p.id}>
                <p className="text-xs font-bold text-[#6D28D9]">{p.title}</p>
                <ul className="mt-1 space-y-1">
                  {p.tasks.map((t) => {
                    const done = data.completedTasks.includes(t.task_id);
                    return <li key={t.task_id} className="flex items-center gap-2 text-sm text-slate-600">{done ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-slate-300" />} {t.title}</li>;
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#5B21B6] to-[#8B5CF6] p-5 text-white shadow-sm" data-testid="motivation-card">
          <QuoteIcon className="h-7 w-7 text-white/40" />
          <p className="mt-3 font-display text-lg font-bold leading-snug">"{quote}"</p>
          <p className="mt-4 text-xs text-purple-200">Motivasi Hari Ini · Scholavia</p>
        </div>
      </div>

      {/* Success stories */}
      <div className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900"><Trophy className="h-4 w-4 text-[#7C3AED]" /> Kisah Sukses</h2>
          <Link to="/app/kisah-sukses" className="text-sm font-semibold text-[#7C3AED] hover:underline">Lihat Semua →</Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {successStories.map((s) => (
            <Link key={s.id} to="/app/kisah-sukses" className="rounded-xl border border-purple-100 p-3 hover:bg-purple-50/40">
              <div className="flex items-center gap-3">
                <img src={s.avatar} alt={s.name} className="h-11 w-11 rounded-full object-cover" />
                <div><p className="text-sm font-bold text-slate-800">{s.name}</p><p className="text-xs text-slate-400">{s.scholarship} · {s.country}</p></div>
              </div>
              <p className="mt-2 text-xs text-slate-500">{s.university}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
